import { supabase, handleSupabaseError } from '@/lib/supabase';
import { Item } from '@/types/models/Item';
import { logger } from '@/lib/logger';
import { StoreUnit } from '@/constants/units';
import { CreateItemDTO } from '@/types';
import { UpdateItemDTO } from '@/types/dtos/ItemDTO';
import { LLMService } from '@/services/ai';
import { getSuggestedCategoriesForStoreType } from '@/constants/storeCategories';

export interface CreateItemOptions {
  name: string;
  name_locale?: string;
  unit: string;
  sell_price: number | string;
  description?: string;
  buy_price?: number | string | null;
  max_retail_price?: number | string | null;
  bar_codes?: string[];
  short_codes?: string[];
  keywords?: string;
  fetchMetadata?: boolean;
  storeUnits: StoreUnit[];
}

export class ItemService {
  static async getAll(storeId: string): Promise<Item[]> {
    try {
      logger.info('Fetching all items', { storeId });
      const pageSize = 1000;
      // First, get the total count
      const { count, error: countError } = await supabase
        .from('store_items')
        .select('*', { count: 'exact', head: true })
        .eq('store_id', storeId)
        .eq('is_deleted', false);
      if (countError) {
        logger.error('Failed to fetch item count', { error: countError, storeId });
        throw countError;
      }
      const totalCount = count || 0;
      logger.debug('Total items:', { storeId, totalCount });
      let allItems: Item[] = [];
      let page = 0;
      while (page * pageSize < totalCount) {
        const from = page * pageSize;
        const to = Math.min((page + 1) * pageSize - 1, totalCount - 1);
        const { data, error } = await supabase
          .from('store_items')
          .select('*')
          .eq('store_id', storeId)
          .eq('is_deleted', false)
          .order('updated_at', { ascending: false })
          .range(from, to);
        if (error) {
          logger.error('Failed to fetch items page', { error, storeId, page });
          throw error;
        }
        if (data && data.length > 0) {
          allItems = allItems.concat(data);
        }
        page++;
      }
      logger.debug('Items fetched successfully', { 
        storeId, 
        itemCount: allItems.length 
      });
      return allItems;
    } catch (error) {
      logger.error('Error in getAll', { error, storeId });
      throw handleSupabaseError(error);
    }
  }

  static async getById(storeId: string, id: string): Promise<Item> {
    try {
      logger.info('Fetching item by ID', { storeId, id });
      
      const { data, error } = await supabase
        .from('store_items')
        .select('*')
        .eq('store_id', storeId)
        .eq('id', id)
        .eq('is_deleted', false)
        .single();

      if (error) {
        logger.error('Failed to fetch item', { error, storeId, id });
        throw error;
      }
      
      if (!data) {
        logger.error('Item not found', { storeId, id });
        throw new Error(`Item not found with ID: ${id}`);
      }

      logger.debug('Item fetched successfully', { storeId, id });
      return data;
    } catch (error) {
      logger.error('Error in getById', { error, storeId, id });
      throw handleSupabaseError(error);
    }
  }

  static async create(
    storeId: string, 
    data: CreateItemDTO, 
    storeMetadata?: { online_store_enabled?: boolean; online_price_enabled?: boolean }
  ): Promise<Item> {
    try {
      if (!data.name || !data.unit || !data.sell_price) {
        throw new Error('Name, unit, and sell price are required');
      }

      // Get store's language_locale if not provided
      let language_locale = data.language_locale;
      if (!language_locale) {
          language_locale = 'hindi';
      }

      // Translate item name if name_locale is not provided or empty
      let name_locale = data.name_locale;
      if (!name_locale || name_locale.trim() === '') {
        try {
          name_locale = await LLMService.translate(data.name, language_locale || 'English');
          logger.debug('Translated item name', { 
            original: data.name, 
            translated: name_locale, 
            language: language_locale 
          });
        } catch (err) {
          logger.warn('Failed to translate item name, using original', { error: err });
          name_locale = data.name;
        }
      }

      // Set online_price based on store metadata (passed from caller)
      // If storeMetadata is not provided, default to 0 (prices hidden)
      const online_price = (storeMetadata?.online_store_enabled && storeMetadata?.online_price_enabled) 
        ? Number(data.sell_price) 
        : 0;

      const { data: item, error } = await supabase
        .from('store_items')
        .insert([{ 
          store_id: storeId, 
          ...((data as unknown) as Record<string, unknown>),
          name_locale,
          online_price
        }])
        .select()
        .single();
      if (error) {
        throw error;
      }
      if (!item) {
        throw new Error('Failed to create item');
      }
      logger.debug('Item created successfully', { 
        storeId, 
        itemId: item.id,
        online_price 
      });
      return item;
    } catch (error) {
      throw handleSupabaseError(error);
    }
  }

  static async update(storeId: string, id: string, data: UpdateItemDTO): Promise<Item> {
    try {
      logger.info('Updating item', { storeId, id, data });
      
      if (data.name === '') {
        logger.error('Invalid update data - empty name', { storeId, id });
        throw new Error('Name cannot be empty');
      }
      if (data.sell_price === 0) {
        logger.error('Invalid update data - zero sell price', { storeId, id });
        throw new Error('Sell price must be greater than 0');
      }

      const { data: item, error } = await supabase
        .from('store_items')
        .update(data)
        .eq('store_id', storeId)
        .eq('id', id)
        .select()
        .single();
        
      if (error) {
        logger.error('Failed to update item', { error, storeId, id, data });
        throw error;
      }
      if (!item) {
        logger.error('Item not found during update', { storeId, id });
        throw new Error(`Item not found with ID: ${id}`);
      }
      logger.debug('Item updated successfully', { storeId, id });
      return item;
    } catch (error) {
      logger.error('Error in update', { error, storeId, id, data });
      throw handleSupabaseError(error);
    }
  }

  static async bulkUpdate(storeId: string, itemIds: string[], data: UpdateItemDTO): Promise<Item[]> {
    try {
      logger.info('Bulk updating items', { storeId, itemIds: itemIds.length, data });
      
      if (itemIds.length === 0) {
        logger.warn('No items to update', { storeId });
        return [];
      }

      // Add updated_at timestamp for better tracking
      const updateData = {
        ...data,
        updated_at: new Date().toISOString()
      };

      const { data: items, error } = await supabase
        .from('store_items')
        .update(updateData)
        .eq('store_id', storeId)
        .in('id', itemIds)
        .select();
        
      if (error) {
        logger.error('Failed to bulk update items', { error, storeId, itemIds: itemIds.length, data });
        throw error;
      }
      
      logger.debug('Items bulk updated successfully', { 
        storeId, 
        itemIds: itemIds.length, 
        updatedCount: items?.length || 0 
      });
      return items || [];
    } catch (error) {
      logger.error('Error in bulk update', { error, storeId, itemIds: itemIds.length, data });
      throw handleSupabaseError(error);
    }
  }

  static async bulkUpdateStockStatus(
    storeId: string, 
    itemIds: string[], 
    isOutOfStock: boolean
  ): Promise<Item[]> {
    try {
      logger.info('Bulk updating stock status', { storeId, itemIds: itemIds.length, isOutOfStock });
      
      if (itemIds.length === 0) {
        logger.warn('No items to update', { storeId });
        return [];
      }

      // Only update the fields we need for stock status
      // The database trigger will handle mutual exclusion (setting is_low_in_stock to false when is_out_of_stock is true)
      const updateData = {
        is_out_of_stock: isOutOfStock,
        ...(isOutOfStock && { available_quantity: 0 }), // Set quantity to 0 when marking out of stock
        updated_at: new Date().toISOString()
      };

      const { data: items, error } = await supabase
        .from('store_items')
        .update(updateData)
        .eq('store_id', storeId)
        .in('id', itemIds)
        .select();
        
      if (error) {
        logger.error('Failed to bulk update stock status', { error, storeId, itemIds: itemIds.length, isOutOfStock });
        throw error;
      }
      
      logger.debug('Stock status bulk updated successfully', { 
        storeId, 
        itemIds: itemIds.length, 
        isOutOfStock,
        updatedCount: items?.length || 0 
      });
      return items || [];
    } catch (error) {
      logger.error('Error in bulk update stock status', { error, storeId, itemIds: itemIds.length, isOutOfStock });
      throw handleSupabaseError(error);
    }
  }

  static async bulkUpdateLowStockStatus(
    storeId: string, 
    itemIds: string[], 
    isLowInStock: boolean
  ): Promise<Item[]> {
    try {
      logger.info('Bulk updating low stock status', { storeId, itemIds: itemIds.length, isLowInStock });
      
      if (itemIds.length === 0) {
        logger.warn('No items to update', { storeId });
        return [];
      }

      // The database trigger will handle mutual exclusion (setting is_out_of_stock to false when is_low_in_stock is true)
      const updateData = {
        is_low_in_stock: isLowInStock,
        updated_at: new Date().toISOString()
      };

      const { data: items, error } = await supabase
        .from('store_items')
        .update(updateData)
        .eq('store_id', storeId)
        .in('id', itemIds)
        .select();
        
      if (error) {
        logger.error('Failed to bulk update low stock status', { error, storeId, itemIds: itemIds.length, isLowInStock });
        throw error;
      }
      
      logger.debug('Low stock status bulk updated successfully', { 
        storeId, 
        itemIds: itemIds.length, 
        isLowInStock,
        updatedCount: items?.length || 0 
      });
      return items || [];
    } catch (error) {
      logger.error('Error in bulk update low stock status', { error, storeId, itemIds: itemIds.length, isLowInStock });
      throw handleSupabaseError(error);
    }
  }

  static async delete(storeId: string, id: string): Promise<void> {
    try {
      logger.info('Soft deleting item', { storeId, id });

      const { error } = await supabase
        .from('store_items')
        .update({ is_deleted: true })
        .eq('store_id', storeId)
        .eq('id', id);

      if (error) {
        logger.error('Failed to soft delete item', { error, storeId, id });
        throw error;
      }

      logger.debug('Item soft deleted successfully', { storeId, id });
    } catch (error) {
      logger.error('Error in soft delete', { error, storeId, id });
      throw handleSupabaseError(error);
    }
  }

  static async search(storeId: string, query: string): Promise<Item[]> {
    try {
      logger.info('Searching items', { storeId, query });

      // First search by name (higher priority)
      const { data: nameResults, error: nameError } = await supabase
        .rpc('store_item_search_by_name', {
          query,
          store_id: storeId
        });

      if (nameError) throw nameError;

      // Then search by keywords and description
      const { data: keywordResults, error: keywordError } = await supabase
        .from('store_items')
        .select('*')
        .eq('store_id', storeId)
        .eq('is_deleted', false)
        .or(`description.ilike.%${query}%,keywords.ilike.%${query}%`)
        .order('updated_at', { ascending: false });

      if (keywordError) throw keywordError;

      // Merge results, removing duplicates by item_id
      const mergedResults = [
        ...(nameResults || []),
        ...((keywordResults || []).filter(kItem => 
          !nameResults?.some((nItem: Item) => nItem.id === kItem.id)
        )).sort((a, b) => b.updated_at.localeCompare(a.updated_at))
      ];

      logger.debug('Items search completed', { 
        storeId, 
        query, 
        resultCount: mergedResults.length 
      });
      
      return mergedResults;
    } catch (error) {
      logger.error('Error in search', { error, storeId, query });
      throw handleSupabaseError(error);
    }
  }

  /**
   * Finds an item by barcode
   * 
   * @param storeId - The store ID to search in
   * @param barcode - The barcode to search for
   * @returns The matching item if found, null otherwise
   */
  static async findByBarcode(storeId: string, barcode: string): Promise<Item | null> {
    try {
      logger.info('Searching item by barcode', { storeId, barcode });

      if (!barcode || !barcode.trim()) {
        return null;
      }

      // Search for items where the barcode is in the bar_codes array
      const { data, error } = await supabase
        .from('store_items')
        .select('*')
        .eq('store_id', storeId)
        .eq('is_deleted', false)
        .contains('bar_codes', [barcode.trim()])
        .limit(1)
        .single();

      if (error) {
        // If no rows found, that's okay - return null
        if (error.code === 'PGRST116') {
          logger.debug('No item found with barcode', { storeId, barcode });
          return null;
        }
        throw error;
      }

      logger.debug('Item found by barcode', { storeId, barcode, itemId: data?.id });
      return data as Item;
    } catch (error) {
      logger.error('Error finding item by barcode', { error, storeId, barcode });
      throw handleSupabaseError(error);
    }
  }

  /**
   * Fetches metadata for an item using AI to enhance item details
   *
   * @param itemName - The name of the item to get metadata for
   * @param storeUnits - Array of valid store units to choose from
   * @param language_locale - The language locale to use for translation (default: 'hindi')
   * @returns Object containing:
   *  - item_name_local: Translated item name in the store's language
   *  - keywords: Array of search-friendly keywords in English and the store's language
   *  - unit: Suggested unit of measurement from the provided storeUnits
   *  - price: Suggested price based on Indian market rates
   * @throws Will throw an error if the AI service call fails
   */
  static async getItemMetadata(
    itemName: string,
    storeType: string = 'General Store',
    storeUnits: StoreUnit[],
    storeCategories: string[] = [],
    language_locale: string = 'hindi',
    existingMetadata?: {
      description?: string;
      keywords?: string;
      price?: number;
      unit?: string;
      alternative_name?: string;
      net_content?: string;
    } | null
  ): Promise<{
    item_name: string;
    item_name_local: string;
    description: string;
    keywords: string[];
    unit: string;
    price: number;
    categories: string[];
  }> {
    try {
      // Use provided language_locale or default to 'hindi'
      const storeLanguage = language_locale.toLowerCase();

      logger.info('Fetching item metadata', { itemName, storeLanguage, storeType, hasExistingMetadata: !!existingMetadata });

      // Get unit names from store configuration
      const unitNames = storeUnits.map(u => u.name.toLowerCase());
      const unitList = unitNames.join('", "');

      // Get suggested categories for the store type
      const suggestedCategories = getSuggestedCategoriesForStoreType(storeType);
      const categoryList = suggestedCategories.join('", "');

      // Build existing store categories context
      let existingCategoriesContext = '';
      if (storeCategories && storeCategories.length > 0) {
        const existingCategoriesList = storeCategories.join('", "');
        existingCategoriesContext = `\n  - Store's existing categories: ["${existingCategoriesList}"] - prefer matching these when they fit.\n`;
      }

      // Build metadata context if available
      let metadataContext = '';
      let nameSuggestionInstruction = '';
      if (existingMetadata) {
        const metadataParts: string[] = [];
        if (existingMetadata.description) {
          metadataParts.push(`Description: ${existingMetadata.description}`);
        }
        if (existingMetadata.keywords) {
          const keywordsList = typeof existingMetadata.keywords === 'string' 
            ? existingMetadata.keywords.split(',').map(k => k.trim()).filter(k => k)
            : existingMetadata.keywords;
          if (keywordsList.length > 0) {
            metadataParts.push(`Keywords: ${Array.isArray(keywordsList) ? keywordsList.join(', ') : keywordsList}`);
          }
        }
        if (existingMetadata.unit) {
          metadataParts.push(`Unit: ${existingMetadata.unit}`);
        }
        if (existingMetadata.price) {
          metadataParts.push(`Price: ${existingMetadata.price}`);
        }
        if (existingMetadata.alternative_name) {
          metadataParts.push(`Alternative name: ${existingMetadata.alternative_name}`);
        }
        if (existingMetadata.net_content) {
          metadataParts.push(`Net content: ${existingMetadata.net_content}`);
        }
        if (metadataParts.length > 0) {
          // Always add unit note when both unit and net_content are present
          let unitNote = '';
          if (existingMetadata.unit && existingMetadata.net_content) {
            unitNote = `\n\nCRITICAL: Unit (${existingMetadata.unit}) vs Net content (${existingMetadata.net_content}): Net content is the measurement inside the package. Unit must be the selling unit: "Piece" for packaged items (bottles, boxes, packets, etc.), or measurement units (Kg, Liter) for bulk/loose items. Example: "300 ml" bottle → unit "Piece" (not "ml").`;
          }
          metadataContext = `\n\nExisting metadata for this item:\n${metadataParts.join('\n')}${unitNote}\n\nUse this information to enhance and refine your output.`;
        }
        
        // Add special instruction for item_name_local when we have alternative name and/or net content
        if (existingMetadata.alternative_name && existingMetadata.net_content) {
          nameSuggestionInstruction = `\n\nIMPORTANT: For "item_name_local", create a concise, user-friendly display name in ${storeLanguage} using the product name, alternative name (if different and meaningful), and net content. Consider all three fields to suggest the best name. Format: "Product Name - Net Content" (e.g., "Masala Munch - 44g"). Use the alternative name only if it adds meaningful information beyond the product name. Keep it short and clear for display in the UI.`;
        } else if (existingMetadata.alternative_name) {
          nameSuggestionInstruction = `\n\nIMPORTANT: For "item_name_local", create a concise, user-friendly display name in ${storeLanguage} using the product name and alternative name. Consider both fields to suggest the best name. Use the alternative name only if it adds meaningful information beyond the product name. Keep it short and clear for display in the UI.`;
        } else if (existingMetadata.net_content) {
          nameSuggestionInstruction = `\n\nIMPORTANT: For "item_name_local", create a concise, user-friendly display name in ${storeLanguage} that combines the product name and net content. Format: "Product Name - Net Content" (e.g., "Masala Munch - 44g"). Keep it short and clear for display in the UI.`;
        }
      }

      const userPrompt = `You are store assistant based out in India.

Given an item name, return a JSON object with the following:
- "item_name": an enhanced name for the product. Leverage the product name, alternative name (if provided), and net content (if provided) to create the best display name. Format: "Product Name - Net Content" when net content is available (e.g., "Masala Munch - 44g"). Use alternative name only if it adds meaningful information. Keep it short and clear.
- "item_name_local": the item name translated into ${storeLanguage} in the native script of the language. Do not drop any information from the item name like variant, weight, size, color, etc.${nameSuggestionInstruction}
- "description": a short, concise description of the product (maximum 12 words). Focus on key product characteristics, brand, or type.
- "keywords": an array of search-friendly keywords in English and ${storeLanguage !== 'english' ? storeLanguage : 'English'} that adheres to below:
  - ALWAYS include the original user-provided item name ("${itemName}") in keywords, especially if suggesting a different item_name, as users may search with the original spelling.
  - the item name with corrected spelling in latin script while keep the pronunciation same.
  - common spoken names and phrases used in Indian stores for that item
  - pronunciation or spelling variants for that item (e.g., "masala" vs "masaala", "chai" vs "chaay")
  - common names of that product in local language but written in latin/roman script (e.g., "namkeen", "chawal", "dal", "atta")
  - popular Indian brands and local product references related to that item
  - regional variations and colloquial terms used across different parts of India
  - Include maximum 8 keywords
- "unit": choose one from ["${unitList}"] based on how the item is typically sold in Indian stores. This is the selling unit, not the measurement unit. 
- "price": suggest a price for the item based on the unit and the item name based on your understanding of the indian market.
- "categories": an array of 1 or 2 specific, relevant category names.${existingCategoriesContext}\n  - Preferred categories for ${storeType} stores: ["${categoryList}"]\n  - If no existing or preferred category fits well, suggest a new appropriate category that follows the same naming style.

Only return the JSON. Do not include any explanation.

Item name: "${itemName}"${metadataContext}`;

      const { data, error } = await supabase.functions.invoke('call-llm', {
        body: {
          userPrompt,
          model: 'gpt-4o-mini'
        }
      });

      if (error) {
        logger.error('Failed to fetch item metadata', { error, itemName });
        throw error;
      }

      // Extract JSON from the code block if present
      const jsonString = data.reply.replace(/^```json\n|\n```$/g, '');
      
      // Parse the JSON response
      const metadata = JSON.parse(jsonString);

      // Ensure item_name is present (fallback to item_name_local or name if not provided)
      if (!metadata.item_name) {
        metadata.item_name = metadata.item_name_local || itemName;
      }

      // Normalize the unit to lowercase
      metadata.unit = metadata.unit.toLowerCase();

      // Ensure description is always present (fallback to item_name_local or name if not provided)
      if (!metadata.description) {
        metadata.description = metadata.item_name_local || metadata.item_name || itemName;
      }
      
      // Normalize description (should be max 12 words)
      metadata.description = typeof metadata.description === 'string' 
        ? metadata.description.trim() 
        : String(metadata.description).trim();
      // Truncate to approximately 12 words if longer
      const words = metadata.description.split(/\s+/);
      if (words.length > 12) {
        metadata.description = words.slice(0, 12).join(' ');
      }

      // Ensure keywords is an array and normalize it
      if (typeof metadata.keywords === 'string') {
        metadata.keywords = metadata.keywords.split(',').map((k: string) => k.trim()).filter((k: string) => k);
      } else if (!Array.isArray(metadata.keywords)) {
        metadata.keywords = [];
      }

      // Filter out any empty keywords
      metadata.keywords = metadata.keywords.filter((k: string) => k && typeof k === 'string' && k.trim().length > 0);

      // Ensure categories is an array and normalize it
      if (!metadata.categories) {
        metadata.categories = [];
      } else if (typeof metadata.categories === 'string') {
        metadata.categories = metadata.categories.split(',').map((c: string) => c.trim()).filter((c: string) => c);
      } else if (!Array.isArray(metadata.categories)) {
        metadata.categories = [];
      }

      // Filter out any empty categories and ensure they're strings
      metadata.categories = metadata.categories
        .filter((c: string) => c && typeof c === 'string' && c.trim().length > 0)
        .map((c: string) => c.trim());

      logger.info('Item metadata fetched successfully', { 
        itemName, 
        metadata 
      });
      
      return metadata;
    } catch (error) {
      logger.error('Error getting item metadata', { error, itemName });
      throw handleSupabaseError(error);
    }
  }

  static async getItemSalesStats(storeId: string, itemId: string): Promise<{
    totalQuantity: number;
    totalRevenue: number;
    invoiceCount: number;
    firstSold: string | null;
    lastSold: string | null;
    avgPrice: number;
    recentSales: Array<{ invoiceId: string; date: string; quantity: number; price: number; customer_name: string; customer_name_locale: string | null; customer_phone: string; }>;
    allSales: Array<{ invoiceId: string; date: string; quantity: number; price: number; customer_name: string; customer_name_locale: string | null; customer_phone: string; }>;
  }> {
    // Aggregate stats from invoice_items and join with invoice
    const { data, error } = await supabase
      .from('invoice_items')
      .select(`
        quantity,
        sell_price,
        invoice_id,
        created_at,
        invoice:invoice_id (
          id,
          creation_time,
          customer_name,
          customer_name_locale,
          customer_phone
        )
      `)
      .eq('item_id', itemId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    if (!data || data.length === 0) {
      return {
        totalQuantity: 0,
        totalRevenue: 0,
        invoiceCount: 0,
        firstSold: null,
        lastSold: null,
        avgPrice: 0,
        recentSales: [],
        allSales: []
      };
    }
    let totalQuantity = 0;
    let totalRevenue = 0;
    let invoiceSet = new Set<string>();
    let firstSold = data[data.length - 1]?.invoice?.creation_time || null;
    let lastSold = data[0]?.invoice?.creation_time || null;
    let priceSum = 0;
    let priceCount = 0;
    const recentSales = data.slice(0, 10).map((row: any) => {
      return {
        invoiceId: row.invoice_id,
        date: row.invoice?.creation_time || row.created_at,
        quantity: row.quantity,
        price: row.sell_price,
        customer_name: row.invoice?.customer_name || 'Unknown',
        customer_name_locale: row.invoice?.customer_name_locale || null,
        customer_phone: row.invoice?.customer_phone || '-',
      };
    });
    const allSales = data.map((row: any) => {
      return {
        invoiceId: row.invoice_id,
        date: row.invoice?.creation_time || row.created_at,
        quantity: row.quantity,
        price: row.sell_price,
        customer_name: row.invoice?.customer_name || 'Unknown',
        customer_name_locale: row.invoice?.customer_name_locale || null,
        customer_phone: row.invoice?.customer_phone || '-',
      };
    });
    for (const row of data) {
      totalQuantity += Number(row.quantity);
      totalRevenue += Number(row.quantity) * Number(row.sell_price);
      invoiceSet.add(row.invoice_id);
      priceSum += Number(row.sell_price);
      priceCount++;
    }
    return {
      totalQuantity,
      totalRevenue,
      invoiceCount: invoiceSet.size,
      firstSold,
      lastSold,
      avgPrice: priceCount ? priceSum / priceCount : 0,
      recentSales,
      allSales
    };
  }


  /**
   * Looks up product details by barcode using external APIs
   * 
   * @param barcode - The barcode to lookup
   * @returns Product details object with name, alternative_name, description, net_content, unit, keywords, price, or null if not found
   */
  static async lookupBarcode(barcode: string): Promise<{
    name: string | null;
    alternative_name: string | null;
    description: string | null;
    net_content: string | null;
    unit: string | null;
    keywords: string | null;
    price: number | null;
  } | null> {
    try {
      logger.info('Looking up barcode', { barcode });

      if (!barcode || !barcode.trim()) {
        logger.warn('Empty barcode provided for lookup');
        return null;
      }

      const { data, error } = await supabase.functions.invoke('barcode-lookup', {
        body: { barcode: barcode.trim() }
      });

      if (error) {
        logger.error('Failed to lookup barcode', { error, barcode });
        // Don't throw - return null to allow graceful degradation
        return null;
      }

      // Check if we got valid product details
      if (data && (data.name || data.description)) {
        logger.info('Barcode lookup successful', { barcode, hasName: !!data.name });
        return {
          name: data.name || null,
          alternative_name: data.alternative_name || null,
          description: data.description || null,
          net_content: data.net_content || null,
          unit: data.unit || null,
          keywords: data.keywords || null,
          price: data.price || null
        };
      }

      logger.debug('No product details found for barcode', { barcode });
      return null;
    } catch (error) {
      logger.error('Error in barcode lookup', { error, barcode });
      // Don't throw - return null to allow graceful degradation
      return null;
    }
  }

  static async deductStockForInvoice(storeId: string, lineItems: Array<{ item_id: string; quantity: number }>): Promise<Array<{
    item_id: string;
    new_available_quantity: number;
    new_is_out_of_stock: boolean;
    new_updated_at: string;
  }>> {
    try {
      logger.info('Deducting stock for invoice', { storeId, lineItemsCount: lineItems.length });

      if (lineItems.length === 0) {
        logger.warn('No line items to process for stock deduction', { storeId });
        return [];
      }

      // Execute single bulk update using RPC function that handles the logic internally
      const { data: updatedFields, error: bulkUpdateError } = await supabase.rpc('bulk_deduct_stock_for_invoice', {
        p_store_id: storeId,
        p_line_items: lineItems.map(item => ({
          item_id: item.item_id,
          quantity: item.quantity
        }))
      });

      if (bulkUpdateError) {
        logger.error('Failed to bulk deduct stock for invoice', { error: bulkUpdateError, storeId, lineItems });
        throw bulkUpdateError;
      }

      logger.info('Bulk stock deduction completed successfully', { 
        storeId, 
        itemsUpdated: updatedFields?.length || 0,
        totalLineItems: lineItems.length
      });

      return updatedFields || [];
    } catch (error) {
      logger.error('Error in deductStockForInvoice', { error, storeId, lineItems });
      throw handleSupabaseError(error);
    }
  }

}